import { useEffect, useRef, useState } from 'react';
import { useAccountStore } from '../../stores/accountStore';
import { useAuthStore } from '../../stores/authStore';
import { receiptsIdentity } from '../../hooks/useOneClickReceipts';
import type { KalshiFill, KalshiOrder, OneClickReceipt } from '../../types/kalshi';

/** #1025 — fill-triggered reads start at least this far apart. */
export const RECEIPT_FILL_REREAD_WINDOW_MS = 1_000;

/** Every fill id for one ticker, as one string. It also changes when one of
 *  them is evicted from the store's bounded window by other markets' fills, so
 *  callers compare for ADDED ids (`addsFill`), never for inequality. */
export function tickerFillSignature(fills: readonly KalshiFill[], ticker: string | null): string {
  if (!ticker) return '';
  let signature = '';
  for (const fill of fills) if (fill.ticker === ticker) signature += `${fill.fillId}|`;
  return signature;
}

/** True when `next` names a fill id that `previous` did not: a fill for this
 *  ticker landed. An eviction only removes ids and is not a fill. */
export function addsFill(previous: string, next: string): boolean {
  const seen = new Set(previous.split('|'));
  return next.split('|').some((id) => id !== '' && !seen.has(id));
}

/** A fill can only move a receipt that is still open at the venue. `unknown`
 *  is not in the set: a read is not a retry, and an unknown attempt stays
 *  unknown until the trader asks. */
export function receiptsAwaitingFill(receipts: readonly OneClickReceipt[]): boolean {
  return receipts.some((receipt) => receipt.state === 'resting' || receipt.state === 'partially_filled');
}

/** #1025 slice 2 — what the open-orders list says about each watched venue
 *  order, as one string (`id=status:remaining|…`, ids sorted) so the store
 *  selector stays a stable primitive. Null — no evidence at all — unless the
 *  account is connected AND this connection has a readable orders snapshot:
 *  `disconnect()` empties the list and a reconnect whose orders read failed
 *  leaves it empty, and neither means every order left the book. */
export function receiptOrdersSignature(
  openOrders: readonly KalshiOrder[] | null | undefined,
  venueOrderIds: readonly string[],
  status: string,
  ordersSyncedMs: number | null,
): string | null {
  if (status !== 'connected' || ordersSyncedMs == null) return null;
  const byId = new Map((openOrders ?? []).map((o) => [o.orderId, o]));
  return [...venueOrderIds].sort().map((id) => {
    const o = byId.get(id);
    // Fill and decrease counts never move without the remaining count moving.
    return `${id}=${o ? `${o.status}:${o.remainingCount}` : 'absent'}`;
  }).join('|');
}

function parseOrdersSignature(signature: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const part of signature.split('|')) {
    if (part === '') continue;
    const at = part.indexOf('=');
    map.set(part.slice(0, at), part.slice(at + 1));
  }
  return map;
}

/** True only when an order watched on BOTH sides changed. The watched set
 *  itself moves with the receipts (a read lands, an order is placed, a receipt
 *  goes final), and none of that is news about an order. */
export function ordersChanged(previous: string | null, next: string | null): boolean {
  if (previous == null || next == null) return false;
  const before = parseOrdersSignature(previous);
  for (const [id, entry] of parseOrdersSignature(next)) {
    const was = before.get(id);
    if (was !== undefined && was !== entry) return true;
  }
  return false;
}

/** Venue order ids of the receipts an order change could still move. */
function watchedOrderIds(receipts: readonly OneClickReceipt[]): string {
  return receipts
    .filter((r) => (r.state === 'resting' || r.state === 'partially_filled') && r.venueOrderId != null)
    .map((r) => r.venueOrderId as string)
    .sort()
    .join(',');
}

interface ReceiptFillRereadArgs {
  ticker: string | null;
  enabled: boolean;
  receipts: readonly OneClickReceipt[];
  /** A read in flight may predate the fill, so a fill during it still re-reads. */
  loading: boolean;
  refresh: () => Promise<void>;
}

interface ReadSlot {
  /** A fill landed that no fill-triggered read has covered yet. */
  pending: boolean;
  /** A fill-triggered read is out. Never start another: each read supersedes
   *  the last in useOneClickReceipts, so overlapping reads can starve the card. */
  inFlight: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  lastStartMs: number;
}

/** Start the pending read if nothing is out and the window allows, re-checking
 *  the CURRENT gate first: a read queued while a receipt was open is dropped if
 *  the card has since become final or unknown. */
function pump(slot: ReadSlot, open: () => boolean, read: () => Promise<void>, onSettled: (slot: ReadSlot) => void): void {
  if (!slot.pending || slot.inFlight || slot.timer != null) return;
  if (!open()) {
    slot.pending = false;
    return;
  }
  const wait = slot.lastStartMs + RECEIPT_FILL_REREAD_WINDOW_MS - performance.now();
  if (wait > 0) {
    slot.timer = setTimeout(() => {
      slot.timer = null;
      pump(slot, open, read, onSettled);
    }, wait);
    return;
  }
  slot.pending = false;
  slot.inFlight = true;
  slot.lastStartMs = performance.now();
  void read().finally(() => {
    slot.inFlight = false;
    onSettled(slot);
  });
}

/**
 * #1025 — the durable receipts card otherwise reads only on mount, ticker
 * change, a one-click ack or Refresh, so a passive order that fills after its
 * own ack kept saying Resting. Re-read when this ticker's fills move.
 *
 * Keyed on the account store's fills, not the push topic, so polled fills
 * count too. The store only ever holds attributed org fills (the relay
 * publishes `fills` after attribution commits), so the read counts them.
 *
 * Single flight: fills that land while a read is out wait for it to settle,
 * then cost one trailing read, no sooner than the window after the last one.
 */
export function useReceiptFillReread({ ticker, enabled, receipts, loading, refresh }: ReceiptFillRereadArgs): void {
  const signature = useAccountStore((s) => tickerFillSignature(s.recentFills, enabled ? ticker : null));
  const watched = watchedOrderIds(receipts);
  const orders = useAccountStore((s) => (enabled && watched !== ''
    ? receiptOrdersSignature(s.openOrders, watched.split(','), s.status, s.ordersSync.lastSuccessMs)
    : null));
  const seenOrders = useRef<{ ticker: string | null; orders: string | null } | null>(null);
  const identity = useAuthStore(receiptsIdentity);
  const mayMove = loading || receiptsAwaitingFill(receipts);
  const seen = useRef<{ ticker: string | null; signature: string } | null>(null);
  const slot = useRef<ReadSlot>({ pending: false, inFlight: false, timer: null, lastStartMs: -Infinity });
  const gate = useRef({ enabled, mayMove });
  const refreshRef = useRef(refresh);
  // Bumped when a fill-triggered read settles, so the trailing decision runs
  // after the render that applied its result, against the gate that produced.
  const [settled, setSettled] = useState(0);
  const open = () => gate.current.enabled && gate.current.mayMove;
  const read = () => refreshRef.current();
  const onSettled = (settledSlot: ReadSlot) => {
    if (slot.current === settledSlot) setSettled((n) => n + 1);
  };

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  // Each active ticket owns its single-flight slot. A slow read from a ticket
  // that was left or disabled must not block the next ticket's fills. The
  // receipt loader already fences late response data; fence this scheduler too.
  // Run before the gate effect so it cannot pump work from the previous scope.
  useEffect(() => () => {
    const current = slot.current;
    if (current.timer != null) clearTimeout(current.timer);
    current.timer = null;
    current.pending = false;
    slot.current = { pending: false, inFlight: false, timer: null, lastStartMs: -Infinity };
  }, [ticker, enabled, identity]);

  // Declared before the fill effect so the gate is current when that runs.
  useEffect(() => {
    gate.current = { enabled, mayMove };
    pump(slot.current, open, read, onSettled);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/read/onSettled only read refs and a setter
  }, [enabled, mayMove, settled]);

  useEffect(() => {
    const previous = seen.current;
    seen.current = { ticker, signature };
    // First sight of a ticker is the mount or ticker-change read's job.
    if (previous == null || previous.ticker !== ticker || !addsFill(previous.signature, signature)) return;
    if (!enabled || !mayMove) return;
    slot.current.pending = true;
    pump(slot.current, open, read, onSettled);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/read/onSettled only read refs and a setter
  }, [ticker, signature, enabled, mayMove]);

  // #1025 slice 2 — a cancel, decrease or an order leaving the book moves a
  // receipt without any fill. Same slot, gate and window as the fill trigger.
  useEffect(() => {
    const previous = seenOrders.current;
    seenOrders.current = { ticker, orders };
    if (previous == null || previous.ticker !== ticker || !ordersChanged(previous.orders, orders)) return;
    if (!enabled || !mayMove) return;
    slot.current.pending = true;
    pump(slot.current, open, read, onSettled);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/read/onSettled only read refs and a setter
  }, [ticker, orders, enabled, mayMove]);
}
